import { create } from 'zustand'
import { ReactNode } from 'react'

export type ToastType = 'success' | 'error' | 'info' | 'loading'

interface Toast {
  id: string
  message: string | ReactNode
  type: ToastType
  /** undefined = no auto-remove (used for `loading`) */
  duration?: number
}

interface State {
  toasts: Toast[]
  addToast: (msg: string | ReactNode, type: ToastType, duration?: number) => string
  removeToast: (id: string) => void
  updateToast: (id: string, message: string | ReactNode, type?: ToastType) => void
}

/** Per-toast auto-remove timer handles, keyed by toast id. */
const timers = new Map<string, ReturnType<typeof setTimeout>>()

const DEFAULT_DURATION: Record<ToastType, number | undefined> = {
  success: 4000,
  error: 4000,
  info: 4000,
  loading: undefined,
}

function scheduleRemoval(id: string, duration: number, remove: (id: string) => void) {
  clearTimer(id)
  timers.set(id, setTimeout(() => {
    timers.delete(id)
    remove(id)
  }, duration))
}

function clearTimer(id: string) {
  const existing = timers.get(id)
  if (existing !== undefined) {
    clearTimeout(existing)
    timers.delete(id)
  }
}

export const useToastStore = create<State>((set, get) => ({
  toasts: [],

  addToast: (message, type, duration) => {
    const id = Math.random().toString(36).slice(2)
    const effectiveDuration = duration ?? DEFAULT_DURATION[type]

    set((s) => ({ toasts: [...s.toasts, { id, message, type, duration: effectiveDuration }] }))

    if (effectiveDuration !== undefined) {
      scheduleRemoval(id, effectiveDuration, get().removeToast)
    }

    return id
  },

  removeToast: (id) => {
    clearTimer(id)
    set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) }))
  },

  updateToast: (id, message, type) => {
    set((s) => ({
      toasts: s.toasts.map((t) => {
        if (t.id !== id) return t
        const newType = type ?? t.type
        const newDuration = DEFAULT_DURATION[newType]
        // Reset the auto-remove timer whenever content changes
        if (newDuration !== undefined) {
          scheduleRemoval(id, newDuration, get().removeToast)
        } else {
          clearTimer(id)
        }
        return { ...t, message, type: newType, duration: newDuration }
      }),
    }))
  },
}))
